import { Card } from "@/shared/ui/card";
import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { Edit, Trash2 } from "lucide-react";
import { useLanguage } from "@/shared/hooks/use-language";
import { useDateFormat } from "@/shared/hooks/use-date-format";
import { formatDateInFilterZone } from "@/shared/lib/date-utils";
import type { UserAccess } from "@/features/auth/api/user-access.ts";

const USER_TYPE_KEY: Record<string, string> = {
  person: "access.typePerson",
  integration: "access.typeIntegration",
  executive: "access.typeExecutive",
  sysadmin: "access.typeSysadmin",
  service: "access.typeService",
  session: "access.typeSession",
  mailbox: "access.typeMailbox",
  open: "access.typeOpen",
};

const USER_STATUS_KEY: Record<string, string> = {
  created: "access.statusCreated",
  active: "access.active",
  blocked: "access.statusBlocked",
  frozen: "access.statusFrozen",
  closed: "access.statusClosed",
};

const STATUS_STYLE: Record<string, string> = {
  active: "bg-green-500/10 text-green-500",
  created: "bg-yellow-500/10 text-yellow-500",
  blocked: "bg-yellow-500/10 text-yellow-500",
  frozen: "bg-yellow-500/10 text-yellow-500",
  closed: "bg-muted text-muted-foreground",
};

const ROLE_STYLE: Record<string, string> = {
  admin: "bg-purple-500/10 text-purple-500",
  operator: "bg-blue-500/10 text-blue-500",
  viewer: "bg-green-500/10 text-green-500",
};

export interface AccessCardProps {
  access: UserAccess;
  isSelf?: boolean;
  // Both handlers are optional together, and their absence is what makes the card read-only.
  // A screen that only reports access -- the Integrations screen's access tab -- passes
  // neither, so the row carries no control whose result would live on another screen.
  onEditRoles?: (access: UserAccess) => void;
  onRevoke?: (access: UserAccess) => void;
}

export const AccessCard = ({ access, isSelf = false, onEditRoles, onRevoke }: AccessCardProps) => {
  const { t } = useLanguage();
  const { dateFormat } = useDateFormat();

  // Both actions only mean something on a live access. Cancel is refused outright by the
  // backend, and editing the roles of a canceled access would not edit anything -- the grant
  // path creates a fresh binding, so the button would silently restore access the History
  // tab is only there to show a record of.
  const isRevoked = access.status !== "active";
  const hasActions = Boolean(onEditRoles || onRevoke);

  const displayName = access.user_name || t("access.unknownUser");
  const displayInitial = access.user_name ? access.user_name.charAt(0).toUpperCase() : "?";
  const displayReference = access.user_reference_id || access.user_id;

  const typeKey = USER_TYPE_KEY[access.user_type];
  const statusKey = access.user_status ? USER_STATUS_KEY[access.user_status] : undefined;

  return (
    <Card className="bg-card border-border p-6" data-testid="access-card">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4 flex-1">
          <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
            <span className="text-lg font-semibold text-primary">{displayInitial}</span>
          </div>

          <div className="flex-1">
            <h3 className="text-lg font-semibold text-foreground mb-1" data-testid="access-user-name">
              {displayName}
              {isSelf && (
                <Badge className="ml-2 bg-primary/10 text-primary" data-testid="access-self">
                  {t("access.you")}
                </Badge>
              )}
              {isRevoked && (
                <Badge
                  className="ml-2 bg-muted text-muted-foreground"
                  data-testid="access-revoked"
                >
                  {t("access.revokedAccess")}
                </Badge>
              )}
            </h3>
            <p className="text-sm text-muted-foreground break-all" data-testid="access-user-reference">
              {displayReference}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
          <div>
            <p className="text-xs text-muted-foreground mb-1">{t("access.userType")}</p>
            <Badge className="bg-slate-500/10 text-slate-500">
              {typeKey ? t(typeKey) : t("access.typeUnknown")}
            </Badge>
          </div>

          {access.roles.length > 0 && (
            <div>
              <p className="text-xs text-muted-foreground mb-1">{t("access.rules")}</p>
              <div className="flex flex-wrap gap-1">
                {access.roles.map(role => (
                  <Badge
                    key={role.id}
                    className={ROLE_STYLE[role.name.toLowerCase()] || "bg-gray-500/10 text-gray-500"}
                  >
                    {role.display_name || role.name}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          <div>
            <p className="text-xs text-muted-foreground mb-1">{t("access.granted")}</p>
            <p className="text-sm text-foreground" data-testid="access-granted">
              {formatDateInFilterZone(access.created, dateFormat)}
            </p>
          </div>

          <div>
            <p className="text-xs text-muted-foreground mb-1">{t("access.status")}</p>
            <Badge
              variant={access.user_status === "active" ? "default" : "secondary"}
              className={
                (access.user_status && STATUS_STYLE[access.user_status])
                || "bg-muted text-muted-foreground"
              }
            >
              {statusKey ? t(statusKey) : t("access.statusUnknown")}
            </Badge>
          </div>

          {hasActions && (
            <div className="flex gap-2">
              {onEditRoles && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { if (!isRevoked) onEditRoles(access); }}
                  aria-disabled={isRevoked || undefined}
                  className={isRevoked
                    ? "text-primary opacity-50 cursor-not-allowed"
                    : "text-primary hover:text-primary/90 hover:bg-primary/10"}
                  title={isRevoked ? t("access.revokedActionsNote") : undefined}
                  aria-label={`${t("access.editUserRoles")} ${displayName}`}
                  data-testid="access-edit"
                >
                  <Edit className="w-4 h-4" />
                </Button>
              )}
              {onRevoke && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { if (!isRevoked) onRevoke(access); }}
                  aria-disabled={isRevoked || undefined}
                  className={isRevoked
                    ? "text-destructive opacity-50 cursor-not-allowed"
                    : "text-destructive hover:text-destructive/90 hover:bg-destructive/10"}
                  title={isRevoked ? t("access.revokedActionsNote") : undefined}
                  aria-label={`${t("access.revokeTitle")} ${displayName}`}
                  data-testid="access-revoke"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};
